import React, { ChangeEvent, useCallback, useEffect, useMemo } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useSessionEntity } from '@context/EntityContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { SegmentedControl } from '@ui/components/SegmentedControl/SegmentedControl'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { isTickmillSCType } from '@utils/companyName.utils'
import { getDomainName } from '@utils/cookie.utils'
import { useWindowResize } from '@utils/domUtils'

import { PlatformInfoDto } from '@model/PlatformInfoDto'
import {
  PlatformTypeEnum,
  PlatformTypeShort,
  formatPlatformName,
  getLandingPlatform,
  getOperatingSystemName,
  getPlatformType,
  isTMTPlatformType,
  platformShortNames,
  sortPlatforms,
} from '@model/PlatformTypeEnum'

import { SelectField } from '@global/field/SelectField'

import { useFetchOne } from '@hooks/useFetch'
import { ScrollToIds } from '@hooks/useScrollToElementIds'

import { Loading } from '@shared/components/Loading/Loading'

import styles from './Platforms.module.scss'

interface UsePlatformInfo {
  platformInfo: PlatformInfoDto[]
  platformId?: PlatformTypeEnum
  selectedPlatform?: PlatformInfoDto
  isLoading: boolean
}

export const usePlatformInfo = (): UsePlatformInfo => {
  const locale = useSessionLanguage()
  const entity = useSessionEntity()
  const location = useLocation()

  const apiClient = useApiClient(ClientApiClient)

  const platformInfoCallback = useCallback(async () => {
    return apiClient.getPlatformInfo({
      domain: getDomainName(true),
      languageId: locale,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale])

  const { data = [], isLoading } = useFetchOne(platformInfoCallback)

  const getPlatformInfo = (platformInfoData: PlatformInfoDto[]) => {
    return platformInfoData.filter((platform) => {
      if (isTMTPlatformType(platform.platfromType.id) && !isTickmillSCType(entity)) {
        return false
      }
      return true
    })
  }

  const platformInfo = useMemo(() => {
    return getPlatformInfo(data)
      .filter(() => !isLoading)
      .filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.platfromType.id === item.platfromType.id)
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const getPlatformId = () => {
    const selectedPlatform = getSelectedPlatformInfo()
    return selectedPlatform?.platfromType?.id
  }

  const getSelectedPlatformInfo = () => {
    const firstPlatform = getLandingPlatform(platformInfo)

    const matchedPlatform = sortedPlatforms?.find((p) =>
      location.pathname.includes(formatPlatformName(p.platfromType.name))
    )

    return matchedPlatform ?? firstPlatform
  }

  const sortedPlatforms = useMemo(
    () =>
      sortPlatforms(platformInfo, [
        PlatformTypeEnum.TradingView,
        PlatformTypeEnum.TickmillTrader,
        PlatformTypeEnum.MT4,
        PlatformTypeEnum.MT5,
        PlatformTypeEnum.None,
      ]),
    [platformInfo]
  )

  return {
    platformInfo: sortedPlatforms,
    isLoading,
    platformId: getPlatformId(),
    selectedPlatform: getSelectedPlatformInfo(),
  }
}

export const Platforms: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const platformInfoProps = usePlatformInfo()
  const { platformId, platformInfo, isLoading } = platformInfoProps

  useEffect(() => {
    if (platformId && platformInfo) {
      const platform = getPlatformType(platformId)
      const platformName = platform.shortName
      const isTickmillTrader = platformName === PlatformTypeShort.TickmillTrader

      // for tmt, only redirect to default OS if user is on base platforms path
      const operatingSystemName = getOperatingSystemName()
      const deviceName = isTickmillTrader ? operatingSystemName : undefined

      const path =
        isTickmillTrader && deviceName
          ? `/dashboard/tools/platforms/${platformName}/${deviceName}`
          : `/dashboard/tools/platforms/${platformName}`

      // if user is already on any TickmillTrader device page
      const alreadyOnTickmillTrader = isTickmillTrader
        ? location.pathname.includes('/tickmilltrader/') &&
          (location.pathname.includes('/android') ||
            location.pathname.includes('/ios') ||
            location.pathname.includes('/webtrader'))
        : location.pathname.includes(platformName)

      const alreadyOnPlatform = platformShortNames.some((platformShortName) =>
        platformShortName === PlatformTypeShort.TickmillTrader
          ? alreadyOnTickmillTrader
          : location.pathname.includes(platformShortName)
      )

      // check if user is on the base platforms path without a specific platform
      const isOnBasePlatformsPath =
        location.pathname === '/dashboard/tools/platforms' ||
        location.pathname.match(/^\/[a-z]{2}\/dashboard\/tools\/platforms$/) !== null

      if (!platformInfo) {
        return
      }

      // we're on a specific platform page (not base path), don't redirect
      if (alreadyOnPlatform && !isOnBasePlatformsPath) {
        return
      }

      // we're not on the base path and we're already on the correct platform path, don't redirect
      if (
        !isOnBasePlatformsPath &&
        (location.pathname.includes(path) ||
          !platformShortNames.includes(platformName as PlatformTypeShort) ||
          platform.shortName === PlatformTypeShort.None)
      ) {
        return
      }

      // Always redirect if we're on the base platforms path
      if (isOnBasePlatformsPath || !alreadyOnPlatform) {
        navigate(path)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformInfo, location.pathname])

  const handlePlatformTabChange = (platformItem: PlatformInfoDto) => {
    const platformName = formatPlatformName(platformItem.platfromType.name)

    if (!platformName) {
      console.warn('Invalid platform name')
      return
    }

    const isTickmillTrader = platformName === PlatformTypeShort.TickmillTrader
    const deviceName = isTickmillTrader ? getOperatingSystemName() : undefined

    const path = deviceName
      ? `/dashboard/tools/platforms/${platformName}/${deviceName}`
      : `/dashboard/tools/platforms/${platformName}`

    navigate(path)
  }

  return (
    <>
      <div id={ScrollToIds.ToolsPlatformsHeader} />
      <Loading showLoadingIcon isLoading={isLoading}>
        <div className={styles.wrapper}>
          <div className={styles.buttonWrapper}>
            <Tabs {...platformInfoProps} onChange={handlePlatformTabChange} />
          </div>
          <Loading showLoadingIcon isLoading={isLoading}>
            <Outlet context={platformInfoProps} />
          </Loading>
        </div>
      </Loading>
    </>
  )
}

interface TabsProps extends ReturnType<typeof usePlatformInfo> {
  onChange(data: PlatformInfoDto): void
}

const Tabs: React.FC<TabsProps> = (props) => {
  const { platformInfo, platformId, onChange } = props

  const isMobile = useWindowResize()

  if (isMobile) {
    const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value
      const selectedPlatformType = platformInfo.find(
        (x) => x?.platfromType?.id.toString() === value.toString()
      )
      selectedPlatformType && onChange(selectedPlatformType)
    }

    return (
      <SelectField
        value={platformId}
        options={platformInfo.map((x) => ({
          label: x.platfromType.name,
          value: x.platfromType.id,
        }))}
        fullWidth
        onChange={handleSelectChange}
      />
    )
  }

  return (
    <div className={styles.tabs}>
      <SegmentedControl
        activeTab={platformId}
        tabs={platformInfo.map((x) => ({
          id: x.platfromType.id,
          name: x.platfromType.name,
          onClick: () => onChange(x),
        }))}
      />
    </div>
  )
}
